import { Link } from 'react-router-dom';
import useReveal from '../hooks/useReveal';
import './ServiceDetail.css';
import { config } from '../config';

const TOOLS = ['Figma','Adobe Illustrator','After Effects','Photoshop','Canva Pro','InDesign','Blender','Framer','Lottie','Spline','Principle','ProtoPie'];

const DESIGN_SERVICES = [
  { icon:'✏️', title:'Brand Identity & Logo Design', desc:'Complete brand systems - logo, typography, colour palette, brand guidelines, and everything needed to launch a consistent identity.' },
  { icon:'📱', title:'UI/UX Design',                  desc:'User research, wireframes, interactive Figma prototypes, and design systems that turn complex products into intuitive experiences.' },
  { icon:'🎬', title:'Motion Graphics & Animation',   desc:'Logo animations, explainer videos, social media reels, and micro-interactions that bring your brand to life.' },
  { icon:'📦', title:'Packaging & Print Design',      desc:'Product packaging, brochures, business cards, banners, and all print collateral - designed to stand out on shelf and in hand.' },
  { icon:'📸', title:'Social Media Graphics',          desc:'Scroll-stopping creatives for Instagram, LinkedIn, Facebook, and YouTube that grow engagement and strengthen brand recall.' },
  { icon:'🖼️', title:'Illustration & Iconography',   desc:'Custom illustrations, icon sets, and visual assets that give your brand a truly unique and ownable visual language.' },
];

const DESIGN_STYLES = [
  { emoji:'⚡', name:'Minimalist',      desc:'Clean, bold, and focused',     bg:'linear-gradient(135deg,#0f0f1a,#1a1a2e)' },
  { emoji:'🌟', name:'Luxury',          desc:'Premium, gold-accented',       bg:'linear-gradient(135deg,#1a1400,#2a2000)' },
  { emoji:'💜', name:'Modern Tech',     desc:'Dark, gradient-forward',       bg:'linear-gradient(135deg,#0d001a,#1a003a)' },
  { emoji:'🌊', name:'Organic',         desc:'Soft, fluid, approachable',   bg:'linear-gradient(135deg,#001a1a,#003333)' },
  { emoji:'🔥', name:'Bold & Vibrant',  desc:'High-energy, eye-catching',   bg:'linear-gradient(135deg,#1a0000,#2a1000)' },
  { emoji:'🪐', name:'Futuristic',      desc:'Sci-fi, cutting-edge',         bg:'linear-gradient(135deg,#000d1a,#001a33)' },
];

const PORTFOLIO = [
  { title:'Brand Identity',   type:'Branding',        emoji:'✨' },
  { title:'FinApp UI Design',       type:'UI/UX Design',    emoji:'📱' },
  { title:'LuxePack Packaging',     type:'Packaging Design',emoji:'📦' },
  { title:'EduLearn Illustrations', type:'Illustration',    emoji:'🎨' },
  { title:'TechCo Motion Reel',     type:'Motion Graphics', emoji:'🎬' },
  { title:'GrowthCo Social Kit',    type:'Social Media',    emoji:'📊' },
];

const PROCESS = [
  { n:'01', title:'Discovery & Brief',  desc:'We deep-dive into your brand personality, target audience, competitor landscape, and design references before starting.' },
  { n:'02', title:'Concept Development',desc:'Multiple creative directions explored - mood boards, sketches, and initial concepts presented for your feedback.' },
  { n:'03', title:'Design & Refine',    desc:'Your chosen direction developed into polished, production-ready designs with up to 3 rounds of revisions.' },
  { n:'04', title:'Delivery',           desc:'Final files delivered in all formats - SVG, PNG, PDF, AI, and Figma - with a full usage guide and brand manual.' },
];

const FAQS = [
  { q:'What design files will I receive?',         a:'You receive all source files (Figma, AI, PSD) plus exported assets in every format you need - PNG, SVG, PDF, WebP, and print-ready files.' },
  { q:'How many revisions are included?',          a:'All projects include 3 rounds of revisions as standard. Additional revision rounds can be added. We aim to get it right within the included rounds.' },
  { q:'Do you create social media templates?',     a:'Yes. We design editable social media templates in Canva or Figma so your team can create consistent on-brand posts independently.' },
  { q:'Can you redesign our existing brand?',      a:'Absolutely. We offer brand refresh and full rebrand services. We start with a brand audit to understand what to evolve and what to keep.' },
];

export default function GraphicsDesign(){
  useReveal();
  const marquee = [...TOOLS,...TOOLS];

  return(
    <div className="sd-page">

      {/* HERO */}
      <section className="sd-hero">
        <div className="sd-hero__bg" style={{background:'radial-gradient(ellipse 70% 60% at 60% 30%,rgba(123,94,167,.08) 0%,transparent 70%),radial-gradient(ellipse 40% 40% at 10% 80%,rgba(201,168,76,.06) 0%,transparent 60%)'}}/>
        <div className="sd-hero__grid"/>
        <div className="container sd-hero__inner">
          <div className="sd-hero__content">
            <span className="sd-eyebrow" style={{background:'rgba(123,94,167,.1)',borderColor:'rgba(123,94,167,.3)',color:'var(--accent)'}}>
              Graphics Design
            </span>
            <h1 className="sd-hero__title">
              Visual Identities That<br/>
              <span className="g">Command Attention</span>
            </h1>
            <p className="sd-hero__sub">
              From brand identity to UI/UX design, motion graphics, and packaging - our creative team crafts visuals that tell your story with impact, emotion, and strategic intent.
            </p>
            <div className="sd-hero__btns">
              <Link to={`${config.appUrl}contact`} className="btn-primary">Start a Design Project →</Link>
              {/* <Link to={`${config.appUrl}contact`} className="btn-outline">View Portfolio</Link> */}
            </div>
            <div className="sd-hero__stats">
              {/* <div className="sd-stat"><span>200+</span><p>Brands Designed</p></div> */}
              <div className="sd-stat"><span>100%</span><p>Satisfaction Rate</p></div>
              <div className="sd-stat"><span>48hr</span><p>First Concept Turnaround</p></div>
            </div>
          </div>
          <div className="sd-hero__visual rr">
            <div className="sd-palette">
              <div className="sd-palette__colors">
                {['#c9a84c','#0f0f1a','#7b5ea7','#4f8ef7','#f4f4f6'].map(c=>(
                  <div key={c} className="sd-palette__swatch" style={{background:c}}/>
                ))}
              </div>
              <div className="sd-palette__cards">
                <div className="sd-palette__card">
                  <div className="sd-palette__icon">✏️</div>
                  <h4>Brand Identity</h4>
                  <p>Logo + Guidelines</p>
                </div>
                <div className="sd-palette__card">
                  <div className="sd-palette__icon">📱</div>
                  <h4>UI/UX Design</h4>
                  <p>Figma Prototypes</p>
                </div>
                <div className="sd-palette__card">
                  <div className="sd-palette__icon">🎬</div>
                  <h4>Motion Design</h4>
                  <p>After Effects</p>
                </div>
                <div className="sd-palette__card">
                  <div className="sd-palette__icon">📦</div>
                  <h4>Packaging</h4>
                  <p>Print-Ready Files</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TOOLS MARQUEE */}
      <div className="marquee-section">
        <div className="marquee-track">
          {marquee.map((t,i)=>(
            <div className="marquee-item" key={i}><span className="dot"/>{t}</div>
          ))}
        </div>
      </div>

      {/* SERVICES */}
      <section className="sd-features">
        <div className="container">
          <div className="tc reveal">
            <span className="s-eye">What We Create</span>
            <h2 className="s-title">Design That Works <span className="g">As Hard As You Do</span></h2>
            <p className="s-body">Every deliverable is crafted with purpose - beautiful, strategic, and built to make your brand unforgettable.</p>
          </div>
          <div className="sd-feat-grid">
            {DESIGN_SERVICES.map((s,i)=>(
              <div key={s.title} className={`sd-feat-card reveal d${(i%3)+1}`}>
                <div className="sd-feat-icon">{s.icon}</div>
                <h3>{s.title}</h3>
                <p>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DESIGN STYLES */}
      <section className="sd-portfolio">
        <div className="container">
          <div className="tc reveal">
            <span className="s-eye">Our Aesthetic Range</span>
            <h2 className="s-title">Every Style. <span className="g">Every Industry.</span></h2>
            <p className="s-body" style={{margin:'1rem auto 0'}}>We adapt to your brand voice - from luxury and minimalist to bold and futuristic.</p>
          </div>
          <div className="gd-styles-grid">
            {DESIGN_STYLES.map((s,i)=>(
              <div key={s.name} className={`gd-style-card reveal d${(i%3)+1}`}>
                <div className="gd-style-card__thumb" style={{background:s.bg}}>
                  <span>{s.emoji}</span>
                </div>
                <div className="gd-style-card__body">
                  <h3>{s.name}</h3>
                  <p>{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PORTFOLIO */}
      {/* <section className="sd-features" style={{background:'var(--bg)'}}>
        <div className="container">
          <div className="tc reveal">
            <span className="s-eye">Our Work</span>
            <h2 className="s-title">Recent <span className="g">Design Projects</span></h2>
          </div>
          <div className="sd-port-grid">
            {PORTFOLIO.map((p,i)=>(
              <div key={p.title} className={`sd-port-card reveal d${(i%3)+1}`}>
                <div className="sd-port-card__top" style={{fontSize:'2.5rem',marginBottom:'1rem'}}>{p.emoji}</div>
                <div className="sd-port-tag">{p.type}</div>
                <h3 style={{marginTop:'1rem'}}>{p.title}</h3>
                <div className="sd-port-arrow" style={{marginTop:'1.2rem'}}>View Project →</div>
              </div>
            ))}
          </div>
        </div>
      </section> */}

      {/* PROCESS */}
      <section className="sd-process">
        <div className="container">
          <div className="tc reveal">
            <span className="s-eye">How We Work</span>
            <h2 className="s-title">Our Design <span className="g">Process</span></h2>
          </div>
          <div className="sd-proc-grid service-graphics">
            {PROCESS.map((p,i)=>(
              <div key={p.n} className={`sd-proc-card reveal d${i+1}`} data-n={p.n}>
                <div className="proc-num">{p.n}</div>
                <h3>{p.title}</h3>
                <p>{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="sd-faq">
        <div className="container">
          <div className="tc reveal">
            <span className="s-eye">Got Questions?</span>
            <h2 className="s-title">Common <span className="g">Questions</span></h2>
          </div>
          <div className="sd-faq-grid">
            {FAQS.map((f,i)=>(
              <div key={i} className={`faq-card reveal d${(i%2)+1}`}>
                <div className="faq-q"><span className="faq-icon">✦</span>{f.q}</div>
                <p className="faq-a">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <div className="cta-sect">
        <div className="cta-glow"/>
        <div className="container" style={{position:'relative',textAlign:'center'}}>
          <div className="reveal">
            <h2 className="cta-title">Ready to Build a Brand <span className="g">Worth Remembering?</span></h2>
            <p className="cta-body">Share your brief and get a free concept direction - no commitment required.</p>
            <div style={{display:'flex',gap:'1rem',justifyContent:'center',flexWrap:'wrap'}}>
              <Link to={`${config.appUrl}contact`} className="btn-primary">Start a Design Project →</Link>
              {/* <Link to={`${config.appUrl}contact`} className="btn-outline">Share Your Brief</Link> */}
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}